import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Heading,
  Spinner,
  Stack,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo } from 'react';
import { Select } from 'shared-components';
import { PhoneNumberInputRHF as PhoneNumberInput } from '../../../atoms/phone-number-input-rhf';
import { CourseCombobox } from '../components/CourseCombobox';
import { ComboboxField, DateInputField, TextInputField, SilentApplicationCheckbox } from '../components/FormFields';
import { useEventHandlers } from '../hooks/useEventHandlers';
import { useCourseData } from '../hooks/useCourseData';
import { ApplicationState } from '@/store';
import { hasPerm, isUniStaffRole, P } from '@/utilities/roles/permissions';
import { useSelector } from 'react-redux';
import { useUniForm } from '../hooks/useApplicationFrom';
import { useHandleIntakeChange } from '../hooks/useHandleIntakeChange';
import { useHandleRegisterApplicantError } from '../hooks/useHandleRegisterApplicantError';
import { useRegisterToast } from '../hooks/useRegisterToast';
import { ApplicationDrawerProps } from '../types';
import { extractErrorMessage, getAutoSelectedSingleOptionValue } from '../utils/courseFiltering';
import { StudyLevel, StudyMode, UniStaffFormKeyNames } from 'types';
import { formFeeStatusOptions, formNationalityOptions } from '../utils/constants';

export function UniStaffApplicationDrawer(props: ApplicationDrawerProps) {
  const { isOpen, onClose, applicationData } = props;

  const submittingRegisterApplicant = useSelector(
    ({ apply }: ApplicationState) => apply.registration.uni.submittingRegisterApplicant,
  );
  const registerApplicantError = useSelector(
    ({ apply }: ApplicationState) => apply.registration.uni.registerApplicantError,
  );

  const role = useSelector((state: ApplicationState) => state.auth.role);
  const universityCode = useSelector((state: ApplicationState) => state.config.university_code);

  const formObj = useUniForm(isOpen);

  // Form specific
  const { form, onSubmit, clearDependentFields, uniOptions } = formObj;

  const {
    watch,
    setValue,
    setError,
    control,
    formState: { errors },
  } = form;

  const watchedUniversityId: string = watch(UniStaffFormKeyNames.UNIVERSITY_ID);
  const watchedIntakeId: string = watch(UniStaffFormKeyNames.INTAKE_ID);
  const watchedStudyLevel: StudyLevel | '' = watch(UniStaffFormKeyNames.STUDY_LEVEL) as StudyLevel | '';
  const watchedFeeStatus: string = watch(UniStaffFormKeyNames.FEE_STATUS);
  const watchedCourseName: string = watch(UniStaffFormKeyNames.COURSE_NAME);
  const watchedMode: StudyMode | '' = watch(UniStaffFormKeyNames.MODE) as StudyMode | '';
  const watchedCampus: string = watch(UniStaffFormKeyNames.CAMPUS);
  const watchedEntryPoint: string = watch(UniStaffFormKeyNames.ENTRY_POINT);

  const courseData = useCourseData(
    watchedUniversityId,
    watchedIntakeId,
    watchedFeeStatus,
    watchedStudyLevel,
    watchedCourseName,
    watchedMode,
    watchedCampus,
    watchedEntryPoint,
  );

  const {
    intakeOptions,
    filteredStudyLevelOptions,
    availableCourses,
    availableModes,
    availableCampuses,
    availableEntryPoints,
    resolvedCourseId,
    loadingCourses,
    loadingApplicationIntakes,
    fetchIntakesForUniversity,
    fetchCoursesForIntake,
  } = courseData;

  // Create a bound version of fetchCoursesForIntake that includes the university
  const fetchCoursesForIntakeWithUni = useCallback(
    (intakeId: string, feeStatus?: string) => {
      fetchCoursesForIntake(watchedUniversityId, intakeId, feeStatus);
    },
    [fetchCoursesForIntake, watchedUniversityId],
  );

  const handlers = useEventHandlers({
    setValue,
    clearDependentFields,
    fetchIntakesForUniversity,
    fetchCoursesForIntake: fetchCoursesForIntakeWithUni,
    feeStatus: watchedFeeStatus,
    universityId: watchedUniversityId,
  });

  const {
    handleUniversityChange,
    handleFeeStatusChange,
    handleIntakeChange,
    handleStudyLevelChange,
    handleCourseNameChange,
    handleModeChange,
    handleCampusChange,
    handleEntryPointChange,
  } = handlers;

  // Auto-select single options for mode, campus, and entry year
  useEffect(() => {
    const autoMode = getAutoSelectedSingleOptionValue(!!watchedCourseName, availableModes, watchedMode);
    if (autoMode) {
      setValue(UniStaffFormKeyNames.MODE, autoMode, { shouldValidate: true });
    }
  }, [availableModes, watchedCourseName, watchedMode, setValue]);

  useEffect(() => {
    const autoCampus = getAutoSelectedSingleOptionValue(!!watchedMode, availableCampuses, watchedCampus);
    if (autoCampus) {
      setValue(UniStaffFormKeyNames.CAMPUS, autoCampus, { shouldValidate: true });
    }
  }, [availableCampuses, watchedMode, watchedCampus, setValue]);

  useEffect(() => {
    const autoEntryPoint = getAutoSelectedSingleOptionValue(!!watchedCampus, availableEntryPoints, watchedEntryPoint);
    if (autoEntryPoint) {
      setValue(UniStaffFormKeyNames.ENTRY_POINT, autoEntryPoint, { shouldValidate: true });
    }
  }, [availableEntryPoints, watchedCampus, watchedEntryPoint, setValue]);

  // Set the resolved course ID when all criteria are selected
  useEffect(() => {
    if (resolvedCourseId) {
      setValue(UniStaffFormKeyNames.COURSE_ID, resolvedCourseId, { shouldValidate: true });
    }
  }, [resolvedCourseId, setValue]);

  // Auto-set university ID for Uni users when drawer opens
  // Note: Don't fetch intakes here - wait for fee status selection (intakes are filtered by fee status on backend)
  useEffect(() => {
    if (isOpen && isUniStaffRole(role) && universityCode && !watchedUniversityId) {
      setValue(UniStaffFormKeyNames.UNIVERSITY_ID, universityCode, { shouldValidate: false });
    }
  }, [isOpen, role, universityCode, setValue, watchedUniversityId]);

  const registerErrorMessage = useMemo(() => extractErrorMessage(registerApplicantError), [registerApplicantError]);

  useHandleIntakeChange('', '', isOpen, setValue, intakeOptions);

  useRegisterToast(onClose);

  // Role specific hook
  useHandleRegisterApplicantError(setError, registerApplicantError);

  // Lock the drawer based on permissions structure not role
  // needs to be at end; hooks cannot be conditional...
  const canCreateApplicant = hasPerm(role, P.CREATE_APPLY_APPLICATION);
  if (!canCreateApplicant) return null;

  return (
    <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="md">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth={1} py={4}>
          <Heading as="h3" size="md">
            {applicationData?.id ? 'Edit Application' : 'Create Applicant'}
          </Heading>
        </DrawerHeader>

        <DrawerBody>
          {registerErrorMessage && (
            <Alert status="error" mb="4">
              <AlertIcon />
              <AlertDescription>{registerErrorMessage}</AlertDescription>
            </Alert>
          )}

          <form noValidate onSubmit={onSubmit}>
            <Stack spacing={6} py={4}>
              {!isUniStaffRole(role) && (
                <Select
                  id={UniStaffFormKeyNames.UNIVERSITY_ID}
                  name={UniStaffFormKeyNames.UNIVERSITY_ID}
                  label="Institution"
                  size="lg"
                  options={uniOptions}
                  value={watchedUniversityId || ''}
                  onChange={handleUniversityChange}
                  errorText={errors.universityId?.message}
                  selectProps={{ isInvalid: !!errors.universityId?.message }}
                />
              )}

              <TextInputField
                control={control}
                errors={errors}
                isSubmitted={form.formState.isSubmitted}
                name={UniStaffFormKeyNames.EMAIL}
                label="Applicant's email address"
                type="email"
              />

              <TextInputField
                control={control}
                errors={errors}
                isSubmitted={form.formState.isSubmitted}
                name={UniStaffFormKeyNames.PREFERRED_NAME}
                label="Preferred name"
              />

              <ComboboxField
                control={control}
                errors={errors}
                isSubmitted={form.formState.isSubmitted}
                name={UniStaffFormKeyNames.NATIONALITY}
                label="Nationality"
                options={formNationalityOptions}
              />

              <DateInputField
                control={control}
                errors={errors}
                isSubmitted={form.formState.isSubmitted}
                name={UniStaffFormKeyNames.DOB}
                label="Date of birth"
              />

              <PhoneNumberInput
                id={UniStaffFormKeyNames.MOBILE_NUMBER}
                name={UniStaffFormKeyNames.MOBILE_NUMBER}
                control={control}
                label="Phone number"
                showErrorsOnlyAfterSubmit
              />

              <Select
                id={UniStaffFormKeyNames.FEE_STATUS}
                name={UniStaffFormKeyNames.FEE_STATUS}
                label="Fee status"
                size="lg"
                options={formFeeStatusOptions}
                value={watchedFeeStatus || ''}
                onChange={handleFeeStatusChange}
                errorText={errors.feeStatus?.message}
                selectProps={{ isInvalid: !!errors.feeStatus?.message }}
              />

              <Select
                id={UniStaffFormKeyNames.INTAKE_ID}
                name={UniStaffFormKeyNames.INTAKE_ID}
                label="Intake"
                size="lg"
                options={intakeOptions}
                value={watchedIntakeId || ''}
                onChange={handleIntakeChange}
                isDisabled={!watchedUniversityId || !watchedFeeStatus || loadingApplicationIntakes}
                isLoading={loadingApplicationIntakes}
                errorText={errors.intakeId?.message}
                selectProps={{ isInvalid: !!errors.intakeId?.message }}
              />

              <Select
                id={UniStaffFormKeyNames.STUDY_LEVEL}
                name={UniStaffFormKeyNames.STUDY_LEVEL}
                label="Study level"
                size="lg"
                options={filteredStudyLevelOptions}
                value={watchedStudyLevel || ''}
                onChange={handleStudyLevelChange}
                isDisabled={!watchedIntakeId || loadingApplicationIntakes}
                errorText={errors.studyLevel?.message}
                selectProps={{ isInvalid: !!errors.studyLevel?.message }}
              />

              <CourseCombobox
                id={UniStaffFormKeyNames.COURSE_NAME}
                name={UniStaffFormKeyNames.COURSE_NAME}
                options={availableCourses}
                value={watchedCourseName || ''}
                onChange={handleCourseNameChange}
                errorText={UniStaffFormKeyNames.COURSE_NAME in errors ? (errors as any).courseName?.message : undefined}
                isDisabled={!watchedUniversityId || !watchedIntakeId || !watchedStudyLevel || loadingCourses}
                isLoading={!!watchedIntakeId && loadingCourses}
                placeholder={
                  !!watchedIntakeId && loadingCourses ? 'Loading courses for selected intake...' : 'Search courses'
                }
              />

              {watchedCourseName && (
                <>
                  <Select
                    id={UniStaffFormKeyNames.MODE}
                    name={UniStaffFormKeyNames.MODE}
                    label="Mode"
                    size="lg"
                    options={availableModes}
                    value={watchedMode || ''}
                    onChange={handleModeChange}
                    isDisabled={availableModes.length === 0}
                    errorText={errors.mode?.message}
                    selectProps={{ isInvalid: !!errors.mode?.message }}
                  />

                  <Select
                    id={UniStaffFormKeyNames.CAMPUS}
                    name={UniStaffFormKeyNames.CAMPUS}
                    label="Campus"
                    size="lg"
                    options={availableCampuses}
                    value={watchedCampus || ''}
                    onChange={handleCampusChange}
                    isDisabled={!watchedMode || availableCampuses.length === 0}
                    errorText={errors.campus?.message}
                    selectProps={{ isInvalid: !!errors.campus?.message }}
                  />

                  <Select
                    id={UniStaffFormKeyNames.ENTRY_POINT}
                    name={UniStaffFormKeyNames.ENTRY_POINT}
                    label="Entry year"
                    size="lg"
                    options={availableEntryPoints}
                    value={watchedEntryPoint || ''}
                    onChange={handleEntryPointChange}
                    isDisabled={!watchedCampus || availableEntryPoints.length === 0}
                    errorText={errors.entryPoint?.message}
                    selectProps={{ isInvalid: !!errors.entryPoint?.message }}
                  />
                </>
              )}

              {loadingCourses && (
                <Flex align="center" mt={2}>
                  <Spinner size="sm" mr={2} />
                  <Box fontSize="sm" color="muted">
                    Loading available courses...
                  </Box>
                </Flex>
              )}

              {!loadingCourses &&
                !watchedCourseName &&
                watchedUniversityId &&
                watchedIntakeId &&
                watchedStudyLevel &&
                availableCourses.length === 0 && (
                  <Box fontSize="sm" color="muted" mt={2}>
                    No courses available for the selected criteria
                  </Box>
                )}

              <SilentApplicationCheckbox
                control={control}
                errors={errors}
                isSubmitted={form.formState.isSubmitted}
                name={UniStaffFormKeyNames.DISABLE_EMAIL_NOTIFICATIONS}
              />

              <Flex justifyContent="flex-end" mt={4}>
                <Button variant="outline" mr={3} onClick={onClose}>
                  Cancel
                </Button>
                <Button colorScheme="blue" variant="solid" type="submit" isLoading={submittingRegisterApplicant}>
                  Create Applicant
                </Button>
              </Flex>
            </Stack>
          </form>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}
